import React, { useEffect, useMemo, useRef, useState } from 'react'
import AppInput from '../../atoms/AppInput/AppInput'
import FilterPanel from '../FilterPanel/FilterPanel'
import { useOperationModes } from 'core/hooks/useOperationModes'
import { SearchFilterValues } from 'interfaces/searchFilter'
import './styles.css'
import { DynamicIcon } from 'components/atoms/Icons'

interface Props {
    onChange: (data: SearchFilterValues) => void
    counts?: {
        all: number
        inProcess: number
        finished: number
        canceled: number
    }
}

const SearchFilter = ({ onChange }: Props) => {
    const { operationModes } = useOperationModes()
    const [filter, setFilter] = useState<SearchFilterValues>({
        search: null,
        states: [],
        date: { start: null, end: null },
        classificationTypeId: null,
        classificationTypeCode: '',
    })
    const [panelOpen, setPanelOpen] = useState(false)
    const panelRef = useRef<HTMLDivElement>(null)

    const onChangeRef = useRef(onChange)
    useEffect(() => { onChangeRef.current = onChange }, [onChange])

    const isMountedRef = useRef(false)

    const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            const target = e.target as HTMLInputElement
            setFilter((prev) => ({ ...prev, search: target.value }))
        }
    }

    const handleState = (value: 'FINISHED' | 'CANCELED' | 'IN_PROCESS') => {
        setFilter((prev) => {
            const exists = prev.states.includes(value)
            return {
                ...prev,
                states: exists
                    ? prev.states.filter((s) => s !== value)
                    : [...prev.states, value],
            }
        })
    }

    const handleApplyPanel = (values: Record<string, unknown>) => {
        const code = (values['classificationTypeCode'] as string) ?? ''
        const meta = values['classificationTypeCode__meta'] as Record<string, unknown> | undefined
        const id = (meta?.id as number | undefined) ?? null
        const updated: SearchFilterValues = {
            ...filter,
            classificationTypeId: id,
            classificationTypeCode: code,
        }
        setFilter(updated)
        setPanelOpen(false)
    }

    const handleResetPanel = () => {
        const updated: SearchFilterValues = {
            ...filter,
            classificationTypeId: null,
            classificationTypeCode: '',
        }
        setFilter(updated)
        setPanelOpen(false)
    }

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                setPanelOpen(false)
            }
        }
        if (panelOpen) document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [panelOpen])

    useEffect(() => {
        if (!isMountedRef.current) {
            isMountedRef.current = true
            return
        }
        onChangeRef.current(filter)
    }, [filter])

    const activeFilterCount = [filter.classificationTypeCode].filter(Boolean).length

    const panelInitialValues = useMemo(
        () => ({ classificationTypeCode: filter.classificationTypeCode ?? '' }),
        [filter.classificationTypeCode]
    )

    return (
        <div className="flex items-center gap-4">
            {/* Izquierda: buscador + botón filtro */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="w-[229px]">
                    <AppInput
                        placeholder="Buscar..."
                        name="search"
                        onKeyDown={handleSearch}
                    />
                </div>

                {/* Botón filtro */}
                <div className="relative" ref={panelRef}>
                    <button
                        type="button"
                        onClick={() => setPanelOpen((o) => !o)}
                        className={`relative flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${
                            activeFilterCount > 0
                                ? 'border-green-500 bg-green-50'
                                : 'border-[#CBD5E1] bg-white hover:bg-gray-50'
                        }`}
                        aria-label="Abrir filtros"
                        aria-expanded={panelOpen}
                    >
                        <DynamicIcon
                            name="filter"
                            className={`w-5 h-5 ${activeFilterCount > 0 ? 'text-green-600' : 'text-gray-500'}`}
                        />
                        {activeFilterCount > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-4 h-4 rounded-full bg-green-500 text-white text-[10px] font-bold leading-none">
                                {activeFilterCount}
                            </span>
                        )}
                    </button>

                    {panelOpen && (
                        <div className="absolute left-0 top-12 z-50 w-72">
                            <FilterPanel
                                onClose={() => setPanelOpen(false)}
                                onApply={handleApplyPanel}
                                onReset={handleResetPanel}
                                initialValues={panelInitialValues}
                            >
                                <FilterPanel.Select
                                    name="classificationTypeCode"
                                    label="Tipo de orden"
                                    options={operationModes.map((m) => ({
                                        value: m.code,
                                        label: m.name,
                                        meta: { id: m.id },
                                    }))}
                                />
                            </FilterPanel>
                        </div>
                    )}
                </div>
            </div>

            {/* Derecha: chips de estado */}
            <div className="flex gap-2 shrink-0">
                <button
                    onClick={() => handleState('IN_PROCESS')}
                    aria-pressed={filter.states.includes('IN_PROCESS')}
                    className={`h-9 px-4 py-2 rounded-full text-sm font-normal leading-[18px] border transition-colors ${
                        filter.states.includes('IN_PROCESS')
                            ? 'bg-gray-800 text-white border-gray-800'
                            : 'bg-white text-[#3E5060] border-[#CBD5E1] hover:bg-gray-50'
                    }`}
                >
                    En proceso
                </button>
                <button
                    onClick={() => handleState('FINISHED')}
                    aria-pressed={filter.states.includes('FINISHED')}
                    className={`h-9 px-4 py-2 rounded-full text-sm font-normal leading-[18px] border transition-colors ${
                        filter.states.includes('FINISHED')
                            ? 'bg-gray-800 text-white border-gray-800'
                            : 'bg-white text-[#3E5060] border-[#CBD5E1] hover:bg-gray-50'
                    }`}
                >
                    Finalizado
                </button>
                <button
                    onClick={() => handleState('CANCELED')}
                    aria-pressed={filter.states.includes('CANCELED')}
                    className={`h-9 px-4 py-2 rounded-full text-sm font-normal leading-[18px] border transition-colors ${
                        filter.states.includes('CANCELED')
                            ? 'bg-gray-800 text-white border-gray-800'
                            : 'bg-white text-[#3E5060] border-[#CBD5E1] hover:bg-gray-50'
                    }`}
                >
                    Cancelado
                </button>
            </div>
        </div>
    )
}

export default SearchFilter
